import React from 'react';
import { Box, Typography, Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { CustomTextField } from './CustomComponents';
import LoadingButton from './LoadingButton';
import urbanImage from '../assets/urban_logo.png';
import { emailMessage, requiredMessage } from '../interfaces/Forms';
import { ApiResponse } from '../interfaces/Networking';

interface EmailForm {
  email: string;
}

function Welcome(): React.ReactElement {
  const navigate = useNavigate();

  const initialValues: EmailForm = {
    email: '',
  };

  const emailSchema = object({
    email: string().required(requiredMessage).email(emailMessage),
  });

  const formik = useFormik<EmailForm>({
    initialValues,
    enableReinitialize: true,
    validationSchema: emailSchema,
    onSubmit: async (values) => {
      const response = await axios.get<ApiResponse<{ exists: boolean }>>(
        `${process.env.REACT_APP_API_URL}/user-exists?email=${values.email}`
      );
      if (response.data.data.exists) {
        // Redirect to Login page
        navigate('/login', { state: { email: values.email } });
      } else {
        // Redirect to Create Account page
        navigate('/create-account', { state: { email: values.email } });
      }
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '100px 20px',
      }}
    >
      <Card
        sx={{
          width: '100%',
          minHeight: '450px',
          maxWidth: '450px',
          padding: '0 40px 40px 40px',
          borderRadius: 4,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <img
            src={urbanImage}
            alt="Urban Utility Logo"
            style={{
              width: '320px',
              objectFit: 'contain',
            }}
          />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Bienvenido
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#535353', mb: 3 }}>
          Ingresa tu email para continuar
        </Typography>
        <form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
          <CustomTextField
            fullWidth
            required
            id="email"
            name="email"
            label="Email"
            placeholder="nombre@email.com"
            value={formik.values.email}
            variant="outlined"
            type="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('email')}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            disableElevation
            loading={formik.isSubmitting}
          >
            Continuar
          </LoadingButton>
        </form>
        <Box
          mt={4}
          textAlign="center"
          sx={{ width: '100%', pt: 2, borderTop: '1px solid #d8d4d4' }}
        >
          <Typography
            variant="body2"
            sx={{
              '& a': {
                textDecoration: 'none',
                color: 'black',
                fontWeight: 600,
              },
              '& a:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Nuevo consignante? &nbsp;<a href="/create-account">Crear cuenta</a>
            <br />
            <a href="/consignar">Cómo funciona</a>
          </Typography>
        </Box>
      </Card>
      <Box
        sx={{
          position: 'relative',
          mt: 2,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          opacity: 0.5,
        }}
      >
        <Typography variant="caption">Guadalajara</Typography>
        <Typography variant="caption">© {new Date().getFullYear()}</Typography>
        <Typography variant="caption">Urban Utility</Typography>
      </Box>
    </Box>
  );
}

export default Welcome;
